/**
 * Hook for browser audio recording via MediaRecorder API.
 * Handles mic permissions, format negotiation (webm/mp4), duration tracking.
 *
 * Two modes share this hook:
 *   - Short capture (default): mic, 60s cap — the Voice Props flow.
 *   - Long segmented capture: pass ``{ maxDurationS: null, source: "tab" }``
 *     for the L10 scribe, which records continuously and rotates segments at
 *     item boundaries via ``rotate()`` (no 60s cap, optional tab-audio source).
 */

import { useState, useRef, useCallback, useEffect } from "react";

const DEFAULT_MAX_DURATION_S = 60;

// Timeslice for ``MediaRecorder.start()`` — emit a ``dataavailable`` slice this
// often so a missed ``stop()`` loses at most one slice, and so the zero-slice
// soft-stall check has a reference interval (a segment older than a couple of
// these with NO non-zero slice is a stall, not silence).
const SLICE_MS = 5000;

/** Where the audio comes from.
 *  - ``mic``     — ``getUserMedia`` only.
 *  - ``tab``     — ``getDisplayMedia`` ("share tab audio" — everyone on the
 *                  call) only; the mic was unavailable / denied.
 *  - ``tab+mic`` — tab audio MIXED with the facilitator's mic via Web Audio,
 *                  for hybrid in-person + remote rooms where the in-room voices
 *                  aren't on the call.
 *  Requesting ``tab`` tries the mixed path first and degrades to ``tab`` or
 *  ``mic`` on partial failure. Requesting ``mic`` only ever yields ``mic``. */
export type RecordingSource = "mic" | "tab" | "tab+mic";

/** What a caller may REQUEST. ``tab+mic`` is output-only (the result of asking
 *  for ``tab`` when a mic is also available) — it's never a valid input, so it's
 *  excluded here to keep callers from passing it and silently getting mic-only. */
export type RequestableSource = "mic" | "tab";

export interface VoiceRecordingOptions {
  /** Auto-stop after this many seconds. ``null`` = no cap (segmented capture). */
  maxDurationS?: number | null;
  /** Audio source to request. Defaults to ``mic`` for the existing short-capture
   *  callers; ``tab`` opts into the tab-audio (+ mic mix) path. */
  source?: RequestableSource;
  /** Called when recording stops due to the OS revoking the share grant
   *  mid-session (user clicked "Stop sharing" in the browser bar). This is
   *  distinct from a caller-initiated ``stop()`` — callers can use this to clean
   *  up their own state without polling ``isRecording``. The ``blob`` argument
   *  is the audio captured since the last segment boundary; callers may upload
   *  it to avoid silently dropping the tail of the recording. */
  onExternalStop?: (blob: Blob) => void;
  /** Called when the recorder DIES without the share grant being revoked — a
   *  mid-session self-heal restart threw (unsupported mime combo, browser quirk,
   *  dead-but-not-revoked stream). Distinct from ``onExternalStop``: the tab
   *  share did NOT end, so the caller should surface a "lost the recorder"
   *  message rather than a "sharing ended" one. Not fired on the initial
   *  ``start()`` construction failure — that surfaces via ``start()`` returning
   *  ``{ ok: false }`` instead. */
  onRecorderFailed?: () => void;
}

/** What ``start()`` resolves to. Returned (not just stored in state) so callers
 *  can branch on failure synchronously — a stale ``error`` closure from the
 *  pre-``setError`` render would otherwise read ``null`` and miss the failure. */
export interface StartResult {
  ok: boolean;
  /** The source that actually won (``tab`` may degrade to ``mic``); null on failure. */
  source: RecordingSource | null;
  /** Failure reason, or null on success. */
  error: string | null;
  /** True when the user explicitly dismissed the browser share prompt
   *  (NotAllowedError / AbortError). Callers should show a softer toast rather
   *  than an error banner — this is a deliberate cancel, not a system failure. */
  cancelled?: boolean;
}

/** What ``rotate()`` resolves to. The blob is the closed segment (empty when
 *  nothing was recording). ``stalled`` disambiguates the two empty-blob cases so
 *  the caller can tell silence from a broken recorder:
 *   - ``false`` — normal: either a real segment, or an empty/sub-threshold one
 *     because the room was silent (drop it quietly).
 *   - ``true``  — the recorder was NOT recording when rotate ran (it had died);
 *     capture was self-healed (a fresh recorder started on the live stream) so
 *     the NEXT segment records, but THIS one captured nothing. Callers should
 *     surface a subtle "no audio captured" signal rather than silently dropping,
 *     and must NOT treat it as a hard stop — capture continues. */
export interface RotateResult {
  blob: Blob;
  stalled: boolean;
}

function getSupportedMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) return "audio/webm;codecs=opus";
  if (MediaRecorder.isTypeSupported("audio/webm")) return "audio/webm";
  if (MediaRecorder.isTypeSupported("audio/mp4")) return "audio/mp4";
  return "";
}

/** What ``acquireStream`` hands back: the stream to RECORD, the source that
 *  actually won, and the underlying resources that ``releaseAcquired`` must tear
 *  down. For a mixed stream the recorded ``stream`` is a synthetic Web-Audio
 *  destination — stopping its tracks does NOT free the tab/mic, so the real
 *  source streams + the AudioContext are tracked here for explicit cleanup. */
interface AcquiredStream {
  /** The stream MediaRecorder records (the mix for ``tab+mic``). */
  stream: MediaStream;
  source: RecordingSource;
  /** Original device streams behind the recorded one — all of them must be
   *  track-stopped on teardown (the mixed destination's tracks are synthetic). */
  sourceStreams: MediaStream[];
  /** Web-Audio graph for the mix, closed on teardown. Null when not mixing. */
  audioContext: AudioContext | null;
  /** The display-capture (tab) stream, if any. Only its tracks represent the
   *  OS share grant — when they end, the tab share was revoked. Mic streams are
   *  excluded because a mic disconnect (Bluetooth drop, unplug) should NOT
   *  terminate the full recording; the tab audio is still valid. */
  displayStream: MediaStream | null;
}

/** Stop every underlying device track and close the mix graph. Safe to call
 *  more than once. */
function releaseAcquired(acquired: AcquiredStream): void {
  acquired.sourceStreams.forEach((s) => s.getTracks().forEach((t) => t.stop()));
  if (acquired.audioContext && acquired.audioContext.state !== "closed") {
    void acquired.audioContext.close();
  }
}

/** Sentinel thrown by ``acquireTabAudio`` (and propagated by ``acquireStream``)
 *  when the user explicitly cancels the share-tab prompt (NotAllowedError /
 *  AbortError). Callers can detect this via ``instanceof UserCancelError``. */
class UserCancelError extends Error {
  constructor() {
    super("user-cancelled");
    this.name = "UserCancelError";
  }
}

/** Prompt the Chrome "share tab" picker and keep ONLY the audio track (we never
 *  touch the shared video). Re-throws NotAllowedError/AbortError as
 *  ``UserCancelError`` so ``acquireStream`` never silently falls back to
 *  mic-only on an explicit user cancel — only on genuine hardware errors. */
async function acquireTabAudio(): Promise<MediaStream> {
  let display: MediaStream;
  try {
    display = await navigator.mediaDevices.getDisplayMedia({
      audio: true,
      // Some browsers reject an audio-only getDisplayMedia; request video too and
      // immediately drop the video tracks so we only keep the tab audio.
      video: true,
    });
  } catch (err) {
    // NotAllowedError (Chrome/Firefox) or AbortError (Safari) — the user hit
    // Cancel on the browser's share prompt. Propagate as UserCancelError so
    // acquireStream() doesn't silently fall back to mic-only.
    if (err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "AbortError")) {
      throw new UserCancelError();
    }
    throw err;
  }
  if (display.getAudioTracks().length === 0) {
    // User shared a tab/window with no audio (or unchecked "share audio").
    display.getTracks().forEach((t) => t.stop());
    throw new Error("no-tab-audio");
  }
  display.getVideoTracks().forEach((t) => {
    t.stop();
    display.removeTrack(t);
  });
  return display;
}

/** Mix tab + mic into one recordable stream via Web Audio: both source streams
 *  feed a single ``MediaStreamAudioDestinationNode`` whose ``.stream`` carries
 *  the blend. Returns the mix plus the graph for teardown. */
function mixStreams(tab: MediaStream, mic: MediaStream): { stream: MediaStream; audioContext: AudioContext } {
  const audioContext = new AudioContext();
  const destination = audioContext.createMediaStreamDestination();
  audioContext.createMediaStreamSource(tab).connect(destination);
  audioContext.createMediaStreamSource(mic).connect(destination);
  // Some browsers start the context suspended (autoplay policy); resume so the
  // graph pumps audio into the destination we record. Fire-and-forget — the
  // start() click is the user gesture that satisfies the policy.
  void audioContext.resume();
  return { stream: destination.stream, audioContext };
}

/** Acquire the audio stream for the requested source. For ``tab`` we try the
 *  hybrid mix (tab + mic) first — when both are available we record the blend so
 *  Kory hears the call AND the in-room voices. On partial failure it degrades:
 *  mic denied → tab-only; tab denied / no audio shared → mic-only. The recorded
 *  source is reported back so the caller's indicator never overclaims. */
async function acquireStream(source: RequestableSource): Promise<AcquiredStream> {
  const canShareTab = typeof navigator.mediaDevices?.getDisplayMedia === "function";
  if (source === "tab" && canShareTab) {
    let tab: MediaStream | null = null;
    try {
      tab = await acquireTabAudio();
    } catch (err) {
      if (err instanceof UserCancelError) {
        // Explicit user cancel — do NOT silently fall back to mic-only.
        // Re-throw so start() can surface a cancel-specific result.
        throw err;
      }
      // Denied / unsupported / no audio shared — fall through to mic-only.
    }
    if (tab) {
      // Tab won; also try the mic so a hybrid room is captured. A denied mic
      // just leaves us tab-only — never fail the whole capture for it.
      let mic: MediaStream | null = null;
      try {
        mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        mic = null;
      }
      if (mic) {
        const { stream, audioContext } = mixStreams(tab, mic);
        return { stream, source: "tab+mic", sourceStreams: [tab, mic], audioContext, displayStream: tab };
      }
      return { stream: tab, source: "tab", sourceStreams: [tab], audioContext: null, displayStream: tab };
    }
  }
  const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
  return { stream: mic, source: "mic", sourceStreams: [mic], audioContext: null, displayStream: null };
}

export function useVoiceRecording(options: VoiceRecordingOptions = {}) {
  const { maxDurationS = DEFAULT_MAX_DURATION_S, source = "mic", onExternalStop, onRecorderFailed } = options;
  // Keep a stable ref to the callback so the track-ended listener (wired at
  // stream-acquire time) always calls the latest closure, even if the caller
  // re-renders.
  const onExternalStopRef = useRef(onExternalStop);
  onExternalStopRef.current = onExternalStop;
  const onRecorderFailedRef = useRef(onRecorderFailed);
  onRecorderFailedRef.current = onRecorderFailed;
  // Tracks which source tracks have already ended — used by the rotate() path
  // to detect a mid-rotate share-grant revoke: if any source track ended while
  // we were rotating, we should not restart a recorder on the dead stream.
  const trackEndedRef = useRef(false);

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // A REAL stall signal — set by the code that actually discovers a dead
  // recorder (the ``beginRecorder`` catch on a mid-session restart, and the
  // rotate hard-dead branch), NOT derived from the isRecording flag (which the
  // scribe flips in lockstep with its own capturing state, so a derived value
  // never fires). Cleared on a successful ``start()`` / ``beginRecorder``.
  const [isStalled, setIsStalled] = useState(false);
  // The source actually in use after acquisition — ``tab`` may degrade to
  // ``mic`` (denied / no audio shared), so callers can surface which one won.
  const [activeSource, setActiveSource] = useState<RecordingSource | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  // The acquisition behind ``streamRef`` — holds the underlying device streams
  // + (for a mix) the AudioContext, so teardown frees the real tab/mic tracks,
  // not just the synthetic mixed-destination tracks.
  const acquiredRef = useRef<AcquiredStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resolveRef = useRef<((blob: Blob) => void) | null>(null);
  // True while a ``rotate()`` is in flight — the ``onstop`` handler must NOT
  // release the stream in that case (we immediately start a fresh recorder on
  // the same stream to continue segmented capture).
  const rotatingRef = useRef(false);
  // False after unmount — the rotate resolver checks this before starting a
  // fresh recorder so a pending ``onstop`` can't construct a recorder on a
  // torn-down stream after the component is gone.
  const mountedRef = useRef(true);
  // Whether ANY non-zero ``dataavailable`` slice arrived for the CURRENT
  // segment. A recorder can sit in ``recording`` state yet emit only zero-size
  // slices (Chromium sample-rate change; an AudioContext never resumed off the
  // gesture) — that closed segment is a soft-stall, not real silence, and must
  // be flagged rather than dropped under the size floor. Reset each time a
  // recorder starts; when a segment closes with this still false after being
  // well past a slice interval, ``rotate()`` reports it stalled.
  const sawAudioRef = useRef(false);
  const segmentStartedAtRef = useRef(0);
  // Serializes rotate()/stop() so a second stop can never land inside the first
  // stop → onstop window and clobber ``resolveRef``. Each call appends its work
  // to this chain; the next op only runs after the previous op's onstop has
  // resolved. (pause/resume and the track-ended 'ended' listener stay OFF the
  // chain — the scribe already awaits rotate() before pause(), and the revoke
  // path resolves through onExternalStop, not a rotate/stop caller.)
  const opChainRef = useRef<Promise<unknown>>(Promise.resolve());

  const isSupported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined" &&
    getSupportedMimeType() !== "";

  // Release the recorded stream AND its underlying device tracks + mix graph.
  // Used by the real-stop path and unmount cleanup; rotate() never calls this.
  const releaseStream = useCallback(() => {
    if (acquiredRef.current) {
      releaseAcquired(acquiredRef.current);
      acquiredRef.current = null;
    } else {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    }
    streamRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      // Mark unmounted BEFORE releasing the stream: a rotate's pending ``onstop``
      // resolver checks ``mountedRef`` and won't start a fresh recorder on the
      // (about-to-be) torn-down stream — that would construct a recorder on
      // stopped tracks, throw, and fire a failure toast on an unmounted tree.
      // ``resolveRef`` is left intact so a pending rotate still resolves (its
      // resolver just skips the fresh-recorder start under ``mountedRef``).
      mountedRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
      releaseStream();
    };
  }, [releaseStream]);

  // Spin up a recorder bound to the current stream + start its data flow. Shared
  // by ``start`` and ``rotate`` so segmented capture reuses one stream. Returns
  // ``true`` on success, ``false`` when construction/start threw — callers
  // decide how to surface that (initial ``start()`` returns ``{ ok: false }``; a
  // mid-session restart fires ``onRecorderFailed``), so this helper never picks
  // the toast itself.
  const beginRecorder = useCallback(
    (stream: MediaStream, mimeType: string): boolean => {
      chunksRef.current = [];
      sawAudioRef.current = false;
      segmentStartedAtRef.current = Date.now();
      let recorder: MediaRecorder;
      try {
        recorder = new MediaRecorder(stream, { mimeType });
        recorderRef.current = recorder;

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunksRef.current.push(e.data);
            sawAudioRef.current = true;
          }
        };

        recorder.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: mimeType });
          if (rotatingRef.current) {
            // Mid-rotation: hand the closed segment to the rotate() resolver and
            // keep the stream alive — a fresh recorder is started by rotate().
            rotatingRef.current = false;
            if (resolveRef.current) {
              resolveRef.current(blob);
              resolveRef.current = null;
            }
            return;
          }
          // Real stop: release the recorded stream, its underlying device tracks,
          // and (for a mix) the AudioContext, plus the timer.
          releaseStream();
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          setIsRecording(false);
          setIsPaused(false);
          setActiveSource(null);
          if (resolveRef.current) {
            resolveRef.current(blob);
            resolveRef.current = null;
          }
        };

        // Timeslice: emit a chunk every ``SLICE_MS`` so ``ondataavailable``
        // streams while recording (``onstop`` still blobs ``chunksRef``, so
        // happy-path output is identical). A missed ``stop()`` then loses at most
        // one slice, not a whole segment.
        recorder.start(SLICE_MS);
        // A live recorder is running — clear any prior stall so a self-heal that
        // succeeds retires the "disconnected" affordance.
        setIsStalled(false);
        return true;
      } catch {
        // MediaRecorder construction or start() threw (unsupported mime combo,
        // dead stream, browser quirk). Surface the death instead of leaving an
        // inactive recorder while the caller still thinks it's capturing: do the
        // real-stop cleanup and flag the stall. We do NOT call onExternalStop
        // here — that path emits a "tab sharing ended" message even when the
        // share is still up (mid-session self-heal / mic-only). The caller reads
        // the ``false`` return and picks the right signal.
        releaseStream();
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setIsRecording(false);
        setIsPaused(false);
        setActiveSource(null);
        setIsStalled(true);
        return false;
      }
    },
    [releaseStream],
  );

  const start = useCallback(async (): Promise<StartResult> => {
    setError(null);
    setDuration(0);
    setIsPaused(false);
    setIsStalled(false);

    const mimeType = getSupportedMimeType();
    if (!mimeType) {
      const msg = "Your browser doesn't support audio recording";
      setError(msg);
      return { ok: false, source: null, error: msg };
    }

    let acquired: AcquiredStream;
    try {
      acquired = await acquireStream(source);
    } catch (err) {
      if (err instanceof UserCancelError) {
        // User dismissed the share prompt — not a hard failure.
        return { ok: false, source: null, error: null, cancelled: true };
      }
      const msg =
        source === "tab"
          ? "Audio capture needs tab-audio or microphone access. Check your browser settings."
          : "Microphone access needed. Check your browser settings.";
      setError(msg);
      return { ok: false, source: null, error: msg };
    }

    const { stream, source: resolvedSource, displayStream } = acquired;
    streamRef.current = stream;
    acquiredRef.current = acquired;
    // The resolved source reflects what actually won: ``tab+mic`` when both were
    // available (hybrid room), or a degrade to ``tab`` / ``mic`` — so the
    // indicator never overclaims what Kory is hearing.
    setActiveSource(resolvedSource);

    // Wire the mid-session "user stopped sharing from the browser bar" event.
    // We ONLY attach to tracks on the display-capture (tab) stream — those
    // represent the OS share grant. Mic tracks are intentionally excluded:
    // a mic disconnect (Bluetooth drop, unplug, headset swap) should NOT kill
    // the whole recording; the tab audio is still valid and the mixer degrades
    // gracefully to silence on the mic channel.
    // For mic-only sessions (displayStream=null) there is no share grant to
    // revoke, so the listener is a no-op.
    const grantStreams = displayStream ? [displayStream] : [];
    trackEndedRef.current = false;
    grantStreams.forEach((s) => {
      s.getTracks().forEach((t) => {
        t.addEventListener("ended", () => {
          trackEndedRef.current = true;
          if (rotatingRef.current) {
            // Mid-rotate: the rotation's onstop will start a fresh recorder on
            // the now-dead stream; the rotate resolver callback (in rotate())
            // checks ``trackEndedRef`` and calls onExternalStop instead. Skip
            // calling stop() here — the recorder is already stopping for the
            // rotation and the onstop handler will run shortly.
            return;
          }
          // Include "paused" — MediaRecorder.stop() is valid from the paused
          // state (per spec, fires onstop), and a paused session whose share
          // grant is revoked must also be cleaned up.
          const recState = recorderRef.current?.state;
          if (recState === "recording" || recState === "paused") {
            // Wire onstop to deliver the final blob to onExternalStop rather
            // than discarding it — the stop() below will trigger onstop, and
            // resolveRef.current is normally null here (no caller awaiting the
            // blob), so we set it to capture and forward the tail segment.
            resolveRef.current = (blob: Blob) => {
              onExternalStopRef.current?.(blob);
            };
            recorderRef.current!.stop();
          }
        });
      });
    });

    if (!beginRecorder(stream, mimeType)) {
      // Initial construction threw — the recorder is dead. beginRecorder already
      // released the stream and flagged the stall; do NOT setIsRecording(true) /
      // return ok (that would paint a success UI over a dead recorder).
      const msg = "Kory couldn't start the recorder. Try again.";
      setError(msg);
      return { ok: false, source: null, error: msg };
    }
    setIsRecording(true);

    // Duration timer. With a cap, auto-stop at the limit; without one
    // (segmented capture) it just ticks for the "capturing" indicator.
    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setDuration(elapsed);
      if (maxDurationS != null && elapsed >= maxDurationS) {
        recorderRef.current?.stop();
      }
    }, 250);

    return { ok: true, source: resolvedSource, error: null };
  }, [source, maxDurationS, beginRecorder]);

  const stop = useCallback((): Promise<Blob> => {
    // Appended to the op chain so a stop can never overwrite ``resolveRef`` while
    // a prior rotate/stop is still waiting on its onstop.
    const run = (): Promise<Blob> =>
      new Promise((resolve) => {
        rotatingRef.current = false;
        resolveRef.current = resolve;
        // Stop from BOTH live states. The scribe's pause() leaves the recorder
        // ``paused`` (grant held, no teardown); a subsequent stop must still fire
        // the native stop so ``onstop`` runs ``releaseStream()`` and frees the
        // mic/tab tracks — otherwise the browser's recording indicator lingers.
        const state = recorderRef.current?.state;
        if (state === "recording" || state === "paused") {
          recorderRef.current!.stop();
        } else {
          // Already inactive — resolve immediately AND clear the single-slot
          // resolver so a stray late onstop can't call a stale resolver (keeps
          // the invariant consistent with the rotation branch, which nulls it).
          resolveRef.current = null;
          resolve(new Blob([], { type: "audio/webm" }));
        }
      });
    const next = opChainRef.current.then(run, run);
    opChainRef.current = next.catch(() => {});
    return next;
  }, []);

  // Close the current segment and immediately start a new one on the SAME
  // stream — the segmented-capture primitive. Resolves the closed segment's
  // blob plus a ``stalled`` flag (see ``RotateResult``). No-op-safe: if nothing
  // is recording it resolves an empty blob (self-healing capture when the stream
  // is still live; a hard-stall signal when it can't).
  const rotate = useCallback((): Promise<RotateResult> => {
    // Appended to the op chain so a rotate can never overwrite ``resolveRef``
    // while a prior rotate/stop is still waiting on its onstop.
    const run = (): Promise<RotateResult> =>
      new Promise((resolve) => {
        const recorder = recorderRef.current;
        const stream = streamRef.current;
        const mimeType = getSupportedMimeType();
        if (recorder?.state !== "recording" || !stream || !mimeType) {
          // The recorder isn't recording. Distinguish a live-but-dead recorder
          // (self-heal so the NEXT segment records) from a genuinely gone stream.
          const live =
            !!stream &&
            !trackEndedRef.current &&
            mountedRef.current &&
            stream.getTracks().some((t) => t.readyState === "live");
          if (live && mimeType) {
            // Self-heal: restart on the still-live stream so capture continues.
            // This segment captured nothing, so flag it stalled — the caller
            // surfaces "no audio captured" but must keep capturing.
            if (beginRecorder(stream, mimeType)) {
              resolve({ blob: new Blob([], { type: mimeType }), stalled: true });
            } else {
              // The restart itself threw — the recorder is hard-dead (not a
              // revoke). beginRecorder already flagged the stall; tell the caller
              // the recorder was lost (source-accurate, not "sharing ended").
              onRecorderFailedRef.current?.();
              resolve({ blob: new Blob([], { type: mimeType }), stalled: true });
            }
          } else {
            // Revoked / dead stream — nothing to restart. Empty, not stalled
            // (the trackEnded / onExternalStop path owns the teardown).
            resolve({ blob: new Blob([], { type: mimeType || "audio/webm" }), stalled: false });
          }
          return;
        }
        rotatingRef.current = true;
        resolveRef.current = (blob: Blob) => {
          if (trackEndedRef.current) {
            // The share-grant track ended while we were rotating — the stream is
            // dead. Don't start a new recorder on it.
            // 1. Perform the real-stop cleanup that onstop's rotation branch
            //    skipped (it returned early without calling releaseStream etc.).
            releaseStream();
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            setIsRecording(false);
            setIsPaused(false);
            setActiveSource(null);
            // 2. Notify onExternalStop with the tail blob so the caller can upload
            //    it; resolve with empty so the rotate() caller's upload chain is a
            //    no-op (double-upload prevention — the tail is now onExternalStop's
            //    responsibility, attributed to the correct item).
            onExternalStopRef.current?.(blob);
            resolve({ blob: new Blob([], { type: mimeType }), stalled: false });
            return;
          }
          // Zero-slice soft-stall: the recorder was nominally ``recording`` yet
          // no non-zero ``dataavailable`` slice arrived for this segment despite
          // running well past a slice interval (Chromium sample-rate change; an
          // AudioContext never resumed off the gesture). The closed blob is
          // essentially empty — indistinguishable from real silence to the size
          // floor — so flag it stalled instead of letting the caller drop it
          // with no signal. Capture continues on the fresh recorder below.
          const segmentMs = Date.now() - segmentStartedAtRef.current;
          const zeroSliceStall = !sawAudioRef.current && segmentMs > SLICE_MS * 2;
          if (mountedRef.current) {
            // Start the next segment on the same stream before handing back the
            // closed one, so capture is continuous across the boundary.
            if (beginRecorder(stream, mimeType)) {
              resolve({ blob, stalled: zeroSliceStall });
            } else {
              // The restart threw — hard-dead recorder (not a revoke). Surface it
              // as a lost-recorder failure, source-accurate.
              onRecorderFailedRef.current?.();
              resolve({ blob, stalled: true });
            }
          } else {
            // Unmounted mid-rotate — do NOT construct a recorder on the stream
            // the unmount cleanup is tearing down. Hand back the closed blob
            // (the caller's own mounted-guard drops any late state write).
            resolve({ blob, stalled: zeroSliceStall });
          }
        };
        recorder.stop();
      });
    const next = opChainRef.current.then(run, run);
    opChainRef.current = next.catch(() => {});
    return next;
  }, [beginRecorder]);

  // Suspend capture WITHOUT tearing down the stream — the MediaStream + its
  // device tracks (and the share-tab grant) stay alive, so resume() never
  // re-prompts. Native ``MediaRecorder.pause()`` holds the open recording; the
  // duration timer is cleared so the cap clock doesn't advance while paused.
  const pause = useCallback(() => {
    if (recorderRef.current?.state !== "recording") return;
    recorderRef.current.pause();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsPaused(true);
  }, []);

  // Resume a paused recording on the SAME stream + recorder. The duration timer
  // is rebuilt so the cap (if any) keeps counting from where it paused; the
  // segmented-capture caller starts a fresh segment of its own on resume.
  const resume = useCallback(() => {
    if (recorderRef.current?.state !== "paused") return;
    recorderRef.current.resume();
    setIsPaused(false);
    // Continue the cap clock from the elapsed-at-pause value rather than
    // resetting it, so a capped capture still stops at its total limit.
    const resumeTime = Date.now();
    const elapsedAtResume = duration;
    timerRef.current = setInterval(() => {
      const elapsed = elapsedAtResume + Math.floor((Date.now() - resumeTime) / 1000);
      setDuration(elapsed);
      if (maxDurationS != null && elapsed >= maxDurationS) {
        recorderRef.current?.stop();
      }
    }, 250);
  }, [duration, maxDurationS]);

  return {
    isRecording,
    isPaused,
    duration,
    isSupported,
    activeSource,
    isStalled,
    start,
    stop,
    rotate,
    pause,
    resume,
    error,
  };
}
